import { HttpHeaders } from '@angular/common/http';
import { BigNumber } from 'bignumber.js';
import moment from 'moment';
import { map, Observable, race, switchMap, takeUntil } from 'rxjs';
import { DataAggregation } from 'src/app/pbir/dataModel';
import { DataType } from 'src/app/pbir/dataModel/DataType';
import { DataQueryExpr } from 'src/app/pbir/dataQuery';
import { Predicate, PredicateType } from 'src/app/pbir/utils/Predicate';
import { ColumnDefinition, DataSourceType2, ExecuteDataQueryElementUsage, FieldSourceType, FilterType, MeasureSourceType, ModelDefinition, NamedTableSourceDefinition, QueryDefinition, QueryExprFieldSourceDefinition, QueryExprMeasureSourceDefinition, ResultSetDataSourceDefinition, ResultSetDefinition, ResultSetElementUsage, SchemaFieldTargetDto, SortDirection, StreamedResultSetDataQueryRequest, TableSourceType, ValueDataType } from 'src/app/services/api.services';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { VisualField } from '../../../VisualField';
import { VisualFieldTarget } from '../../../VisualFieldTarget';
import { VisualDataAdapter } from '../../VisualDataAdapter';
import { RowsResult, VisualDataQueryColumn, VisualDataQueryColumnCollection, VisualDataResource } from '../../VisualDataSource';
import { ColumnImpl } from './ColumnImpl';
import { StreamedResultSetDataQueryResult, TableImpl } from './TableImpl';
import { encodeCacheKey } from 'src/app/pbir/utils/cacheKey';
import { logger } from '../../../../utils/logger';

const LOGGER = logger('AdapterImpl');

export const RESULTSET_MODEL_TABLE = 'resultSet';

export class AdapterImpl extends VisualDataAdapter {
  constructor(public readonly dataTable: TableImpl) { super(dataTable); }
  
  get injector() { return this.dataTable.injector; }
  get cache() { return this.dataTable.cache; }
  get dataQueryService() { return this.dataTable.dataQueryService; }
  get httpClient() { return this.dataTable.httpClient; }

  get request() { return this.dataTable.request; }
  get queryId() { return this.dataTable.queryId; }
  get resultSet() { return this.dataTable.resultSet; }

  protected calculateImpl(
    r: VisualDataAdapter.CalculateRequest,
    resultTransform?: (r: RowsResult) => RowsResult): VisualDataResource<RowsResult> {
    this.clearFieldHelperCache();

    let resourceKey = this.getResourceKey(r);
    let factory = (aborted) => {
      let query = this.createQuery(r);
      
      return this.dataQueryService.executeStreamedResultSetQuery({
        queryId: this.queryId,
        resultSetId: this.resultSet.id,
        query,
        currencyCode: this.request.currency?.currencyCode,
        currencyName: this.request.currency?.currencyName,
        skip: r.skip,
        take: r.take,
      }).pipe(
        takeUntil(aborted),
        switchMap(x => this.createStreamedResultSetDataQueryRequest(x).pipe(
          takeUntil(aborted),
          map(({ rows, resultSet }) => <RowsResult>{
            columns: this.mapColumns(r, resultSet),
            rows
          }),
          map(x => resultTransform ? resultTransform(x) : x)
        )),
      );
    };

    return this.dataTable.resources.getOrCreate(
      this.dataTable, 
      resourceKey,
      factory, 
      { noCache: r.noCache, debugName: r.debugName, abortSignal: r.abortSignal });
  }

  private getResourceKey(r: VisualDataAdapter.CalculateRequest) {
    return [
      this.sortKeyPart(r.fields?.map(f => [
        f.name ?? '', 
        this.normalizeExpr(f.expr),
        f.dataType?.value ?? ValueDataType.Unknown,
        f.return ?? false,
        f.grouping ?? false,
      ]) ?? []),

      this.sortKeyPart(r.measures?.map(m => [
        m.name ?? '', 
        this.normalizeExpr(m.expr),
        m.dataType?.value ?? ValueDataType.Unknown,
        m.return ?? false,
      ]) ?? []),

      this.sortKeyPart(r.filters?.map(f =>
        this.normalizePredicate(f)
      ) ?? []),

      this.sortKeyPart(r.order?.map((o, i) => [
        o.sortOrder ?? i,
        this.normalizeTarget(this.targetOf(o)),
        o.sortDirection ?? 'null',
      ].sort((a, b) => a[0] - b[0])) ?? []),

      r.skip ?? 0,
      r.take ?? '*',
    ];
  }
  
  private normalizeExpr(expr: string) {
    try {
      return DataQueryExpr.parse(expr?.trim(), { throwOnError: true }).toString();
    }
    catch (err) {
      LOGGER.error(`Failed to normalize expr string`, { expr, err });
      return `<<ERROR>>${expr}`;
    }
  }

  private normalizePredicate(f: Predicate<VisualDataAdapter.Field>): any[] {
    if (f.type === PredicateType.Advanced) {
      return [
        FilterType.Advanced,
        this.normalizeTarget(f.target?.target),
        f.logicalOperator ?? '',
        f.conditions?.map(x => [
          x.type ?? '', 
          x.value ?? '<<null>>'
        ]) ?? []
      ];
    }
    else if (f.type === PredicateType.Basic) {
      return [
        FilterType.Basic,
        this.normalizeTarget(f.target?.target),
        f.operator ?? '',
        f.values ?? []
      ];
    }
    else if (f.type === PredicateType.Dynamic) {
      return [
        FilterType.Dynamic,
        this.normalizeTarget(f.target?.target),
        f.kind ?? '',
      ];
    }
    else if (f.type === PredicateType.RelativeDate) {
      return [
        FilterType.RelativeDate,
        this.normalizeTarget(f.target?.target),
        f.operator ?? '',
        f.firstUnitCount ?? '',
        f.firstUnitType ?? '',
        f.secondUnitCount ?? '',
        f.secondUnitType ?? '',
        f.includeToday ?? false
      ];
    }
    else if (f.type === PredicateType.RelativeTime) {
      return [
        FilterType.RelativeTime,
        this.normalizeTarget(f.target?.target),
        f.operator,
        f.firstUnitCount ?? '',
        f.firstUnitType ?? '',
        f.secondUnitCount ?? '',
        f.secondUnitType ?? '',
      ];
    }
    else if (f.type === PredicateType.TopN) {
      return [
        FilterType.TopN,
        this.normalizeTarget(f.target?.target),
        f.operator ?? '',
        f.maxCount ?? ''
      ];
    }
    else if (f.type === PredicateType.Expr) {
      return [
        FilterType.Expr,
        this.normalizeTarget(f.target?.target),
        f.expr?.trim()
      ];
    }

    throw new Error(`Not implemented`, { cause: f });
  }

  private normalizeTarget(t: VisualField | VisualFieldTarget) {
    let target = t instanceof VisualField ? t.target : t;
    return target?.identifierExpr.toLowerCase() ?? '';
  }

  private sortKeyPart(part: any[][]): any[] {
    return part.map(x => encodeCacheKey(JSON.stringify(x))).sort((a, b) => a.localeCompare(b));
  }

  private mapColumns(
    r: VisualDataAdapter.CalculateRequest,
    resultSet: ResultSetDefinition
  ) {
    return new VisualDataQueryColumnCollection(
      ...resultSet.elements.map((e, i) => {
        let name = e.name.toLowerCase();

        let f = r.fields?.find(x => x.name.toLowerCase() == name)
          ?? r.measures?.find(x => x.name.toLowerCase() == name);

        return <VisualDataQueryColumn>{
          index: i,
          dataIndex: i,
          dataType: f?.dataType,

          name,
          identifier: `[${RESULTSET_MODEL_TABLE}].[${e.dataColumn}]`,

          usage: 
            e.usage == ResultSetElementUsage.GroupingField ? ExecuteDataQueryElementUsage.Grouping :
            e.usage == ResultSetElementUsage.Aggregation   ? ExecuteDataQueryElementUsage.Measure  : ExecuteDataQueryElementUsage.Other,

          target: null,
        };
      })
    );
  }


  protected getDistinctValueCalculation(
    fields: VisualDataAdapter.FieldCalculation[],
    measures: VisualDataAdapter.Calculation[],
    f: VisualDataAdapter.Field
  ) {
    let h = this.getFieldHelper(f);

    fields.push(h.getCalculation({
      return: true,
      grouping: true,
    }));
  }

  protected getAggregationCalculation(
    fields: VisualDataAdapter.FieldCalculation[],
    measures: VisualDataAdapter.Calculation[],
    f: VisualDataAdapter.Field, 
    aggregation: DataAggregation
  ) {
    let h = this.getFieldHelper(f);
    
    let measureName =
      `_m${aggregation
        .formatAggregationOfText(h.identifier)
        .replace(/[^a-z0-9_]/gi, '_')}`;

    let measureType = aggregation.getResultDataType(h.column.dataType);

    fields.push(h.getCalculation({
      dataType: measureType,
      return: false,
      grouping: false,
    }));

    measures.push({
      name: measureName,
      expr: aggregation.formatDataQueryExpr(h.referenceSyntax),
      dataType: measureType,
      return: true,
    });
  }


  private createQuery(r: VisualDataAdapter.CalculateRequest): QueryDefinition {
    let fi = 1;
    let mi = 1;
    
    return {
      model: this.getResultSetModel(),

      fields: r.fields?.map(f => ({
        name: f.name ?? `__f${fi++}`,
        source: <QueryExprFieldSourceDefinition>{
          type: FieldSourceType.QueryExpr,
          expr: f.expr,
          format: {
            type: f.dataType.value,
            nullable: true
          },
        },
        return: f.return,
        grouping: f.grouping,
      })),
      
      measures: r.measures?.map(m => ({
        name: m.name ?? `__m${mi++}`,
        source: <QueryExprMeasureSourceDefinition>{
          type: MeasureSourceType.QueryExpr,
          expr: m.expr,
          format: {
            type: m.dataType.value,
            nullable: true
          },
        },
        return: m.return,
      })),

      filters: r.filters?.map(f => ({
        expr: <any>this.filterToExpr(f)
      }))
    };
  }
  
  private filterToExpr(f: Predicate<VisualDataAdapter.Field>): string {
    if (f.type == PredicateType.Expr) {
      // TODO: ExprFilter -> QueryExpr
      return f.expr;
    }

    let t = this.getFieldHelper(f.target);

    if (f.type == PredicateType.Basic) {
      if (f.operator == Predicate.BasicOperator.All)
        return null;

      let values = f.values
        .map(v => this.getValueLiteral(v))
        .join(', ');

      let syntax = `${t.referenceSyntax} IN {${values}}`;

      if (f.operator == Predicate.BasicOperator.NotOneOf) {
        syntax = `NOT(${syntax})`;
      }

      return syntax;
    }

    if (f.type == PredicateType.Advanced) {
      let conditions = f.conditions
        .map(c => {
          let value = this.getValueLiteral(c.value);

          switch (c.type) {
            case Predicate.ConditionType.IsNot:
              return `${t.referenceSyntax} <> ${value}`;

            case Predicate.ConditionType.Is:
              return `${t.referenceSyntax} == ${value}`;

            case Predicate.ConditionType.IsNotBlank:
              return `NOT(IS_NULL_OR_BLANK(${t.referenceSyntax}))`;

            case Predicate.ConditionType.IsBlank:
              return `IS_NULL_OR_BLANK(${t.referenceSyntax})`;

            case Predicate.ConditionType.GreaterThan:
              return `(${t.referenceSyntax}) > ${value})`;

            case Predicate.ConditionType.GreaterThanOrEqual:
              return `(${t.referenceSyntax}) >= ${value})`;

            case Predicate.ConditionType.LessThan:
              return `(${t.referenceSyntax}) < ${value})`;

            case Predicate.ConditionType.LessThanOrEqual:
              return `(${t.referenceSyntax}) <= ${value})`;

            case Predicate.ConditionType.DoesNotContain:
              return `NOT(STR_FIND(${t.referenceSyntax}, ${value}) > 0)`;

            case Predicate.ConditionType.Contains:
              return `(STR_FIND(${t.referenceSyntax}, ${value}) > 0)`;

            case Predicate.ConditionType.DoesNotStartWith:
              return `NOT(STR_FIND(${t.referenceSyntax}, ${value}) == 1)`;

            case Predicate.ConditionType.StartsWith:
              return `(STR_FIND(${t.referenceSyntax}, ${value}) == 1)`;
          }
        });

      return conditions.join(
        (f.logicalOperator === Predicate.LogicalOperator.And) ? ' AND ' : ' OR '
      );
    }

    if (f.type == PredicateType.RelativeDate) {
    }

    if (f.type == PredicateType.RelativeTime) {

    }

    if (f.type == PredicateType.Dynamic) {

    }

    if (f.type == PredicateType.TopN) {

    }
    
    throw new Error(`Not implemented '${PredicateType[f.type]}'`);
  }

  private getValueLiteral(value: any, dataType?: DataType) {
    if (!dataType) {
      if (typeof value === 'boolean') 
        dataType = DataType.Boolean;

      if (typeof value === 'number' ||
          typeof value === 'bigint' || BigNumber.isBigNumber(value)) {
        dataType = DataType.Number;
      }

      if (value instanceof DataQueryExpr.DateValue) 
        dataType = DataType.DateOnly;
        
      if (value instanceof DataQueryExpr.TimeValue) 
        dataType = DataType.TimeOnly;
        
      if (value instanceof Date ||
          value instanceof DataQueryExpr.DateTimeValue ||
          moment.isMoment(value)) {
        dataType = DataType.DateTime; 
      }

      if (typeof value === 'string') 
        dataType = DataType.Text;
    }

    if (dataType.isBoolean) {
      if (value === false)
        return 'FALSE';
        
      if (value === true)
        return 'TRUE';

      return `TO_BOOL(${stringLiteral(value)})`;
    }

    if (dataType.isNumeric) {
      if (typeof value === 'number' ||
          typeof value === 'bigint') {
        return value.toString();
      }

      if (BigNumber.isBigNumber(value)) {
        return value.toString();
      }

      return `TO_NUMBER(${stringLiteral(value)})`;
    }

    if (dataType.isDateOnly) {
      if (value instanceof Date) {
        return `MAKE_DATE(${value.getFullYear()}, ${value.getMonth()}, ${value.getDay()})`;
      }

      if (moment.isMoment(value)) {
        return `MAKE_DATE(${value.year()}, ${value.month()}, ${value.day()})`;
      }
      
      if (value instanceof DataQueryExpr.DateTimeValue) {
        value = value.date;
      }

      if (value instanceof DataQueryExpr.DateValue) {
        return `MAKE_DATE(${value.year.value}, ${value.month.value}, ${value.day.value})`;
      }
      
      return `TO_DATE(${stringLiteral(value)})`;
    }

    if (dataType.isTimeOnly) {
      if (value instanceof Date) {
        return `MAKE_TIME(${value.getHours()}, ${value.getMinutes()}, ${value.getSeconds()}, ${value.getMilliseconds()})`;
      }

      if (moment.isMoment(value)) {
        return `MAKE_TIME(${value.hour()}, ${value.minute()}, ${value.second()}, ${value.millisecond()})`;
      }
      
      if (value instanceof DataQueryExpr.DateTimeValue) {
        value = value.time;
      }

      if (value instanceof DataQueryExpr.TimeValue) {
        return `MAKE_TIME(${value.hour.value}, ${value.minute.value}, ${value.second.value}, ${value.fraction.value})`;
      }
      
      return `TO_TIME(${stringLiteral(value)})`;
    }

    if (dataType.isDateTime) {
      if (value instanceof Date) {
        return `MAKE_DATETIME(${value.getFullYear()}, ${value.getMonth()}, ${value.getDay()}, ${value.getHours()}, ${value.getMinutes()}, ${value.getSeconds()}, ${value.getMilliseconds()})`;
      }

      if (moment.isMoment(value)) {
        return `MAKE_DATETIME(${value.year()}, ${value.month()}, ${value.day()}, ${value.hour()}, ${value.minute()}, ${value.second()}, ${value.millisecond()})`;
      }
      
      if (value instanceof DataQueryExpr.DateValue) {
        return `MAKE_DATETIME(${value.year.value}, ${value.month.value}, ${value.day.value}, 0, 0, 0, 0)`;
      }

      if (value instanceof DataQueryExpr.DateTimeValue) {
        return `MAKE_DATETIME(${value.date.year.value}, ${value.date.month.value}, ${value.date.day.value}, ${value.time.hour.value}, ${value.time.minute.value}, ${value.time.second.value}, ${value.time.fraction.value})`;
      }

      return `TO_DATETIME(${stringLiteral(value)})`;
    }

    if (dataType.isText) {
      return stringLiteral(value);
    }

    return value;


    function stringLiteral(value: string) {
      if (isNullOrUndefined(value))
        return 'NULL';
        
       return `'${value?.replaceAll("'", "\\'")}'`;
    }
  }


  private readonly fieldHelperCache = new Map<any, FieldHelper>();

  private clearFieldHelperCache() {
    this.fieldHelperCache.clear();
  }

  private getFieldHelper(f: VisualDataAdapter.Field): FieldHelper {
    let k = `${f.target.aggregation?.value ?? ''}||${f.target.identifierExpr}`;

    let h = this.fieldHelperCache.get(k);
    if (!h) {
      let column = this.dataTable.columns.get(f.target);
      let identifier = `_f${column.index}__${f.target.formatLongTitle('_').replace(/[^a-z0-9]/gi, '')}`;

      h = new FieldHelper(
        this.targetOf(f),
        column,
        identifier,
        `[${identifier}]`,
        {
          name: identifier,
          expr: this.getColumnReferenceSyntax(column),
          dataType: f.target.dataType,
          return: false,
          grouping: false,
        });

      this.fieldHelperCache.set(k, h);
    }

    return h;
  }

  private resultSetModel: ModelDefinition;

  private getResultSetModel() { return this.resultSetModel ??= this.createResultSetModel(); }

  private createResultSetModel(): ModelDefinition {
    let resultSet = this.dataTable.resultSet;
    let resultSetString = `ResultSet${resultSet.id}`;
    
    return {
      schemas: [
        {
          name: resultSetString,

          source: <ResultSetDataSourceDefinition>{
            type: DataSourceType2.ResultSet,
            resultSetId: resultSet.id
          },

          tables: [
            {
              name: RESULTSET_MODEL_TABLE,

              source: <NamedTableSourceDefinition>{
                type: TableSourceType.Named,
                table: resultSetString,
              },

              columns: [
                {
                  name: '__rid',
                  source: <QueryExprFieldSourceDefinition>{
                    type: FieldSourceType.QueryExpr,
                    expr: `SOURCE_COLUMN([${RESULTSET_MODEL_TABLE}], '__rid')`,
                    format: {
                      type: ValueDataType.Integer,
                      nullable: false
                    }
                  }
                },

                ...resultSet.elements.map(e => <ColumnDefinition>{
                  name: e.dataColumn,
                  source: <QueryExprFieldSourceDefinition>{
                    type: FieldSourceType.QueryExpr,
                    expr: `SOURCE_COLUMN([${RESULTSET_MODEL_TABLE}], '${e.dataColumn}')`,
                    format: e.format
                  }
                })
              ],

              keyColumns: [
                '__rid'
              ]
            }
          ],

          factTable: RESULTSET_MODEL_TABLE,
        }
      ],

      relationships: []
    };
  }

  private getColumnReferenceSyntax(column: ColumnImpl) {
    return `[${RESULTSET_MODEL_TABLE}].[${column.resultSetElement.dataColumn}]`;
  }

  private createStreamedResultSetDataQueryRequest(
    request: StreamedResultSetDataQueryRequest
  ): Observable<StreamedResultSetDataQueryResult> {
    let { method, headers, url, body } = request.url;

    return this.httpClient.request(method, url, {
      headers: new HttpHeaders(headers),
      responseType: 'text',
      body,

      transferCache: false,
      withCredentials: false,
    }).pipe(
      map((r: string) => {
        let json = JSON.parse(r);

        let dataArray = <any[]>json;
        let resultSet = <ResultSetDefinition>dataArray[0];

        let rows = dataArray;
        rows.splice(0, 1);

        return { resultSet, rows };
      })
    );
  }
}

class FieldHelper {
  constructor(
    readonly target: VisualFieldTarget,
    readonly column: ColumnImpl,
    readonly identifier: string,
    readonly referenceSyntax: string,
    readonly calculation: VisualDataAdapter.FieldCalculation) { }

  getCalculation(options?: {
    dataType?: DataType;
    return?: boolean;
    grouping?: boolean;
  }): VisualDataAdapter.FieldCalculation {
    return {
      ...this.calculation,
      dataType: options?.dataType ?? this.calculation.dataType,
      return: !!options?.return,
      grouping: !!options?.grouping,
    };
  }
}
